import { lazy, Suspense } from "react";
import { Navigate, RouteObject } from "react-router-dom";
import { Loader } from "../components/loader/Loader";
import { AppLinks } from "../links";
import { RouteDefinition } from "./RouteDefinition";
import { RoutePolicy } from "./RoutePolicy";
import { HomeRedirect } from "../pages/home/HomeRedirect";

const publicRoutes = (routes: RouteObject[]): RouteDefinition[] =>
  routes.map((route) => ({
    ...route,
    element: <Suspense fallback={<Loader />}>{route.element}</Suspense>,
    policy: RoutePolicy.ForGuests,
  }));

const privateRoutes = (routes: RouteObject[]): RouteDefinition[] =>
  routes.map((route) => ({
    ...route,
    element: <Suspense fallback={<Loader />}>{route.element}</Suspense>,
    policy: RoutePolicy.ForAuthenticated,
  }));

const anyoneRoutes = (routes: RouteObject[]): RouteDefinition[] =>
  routes.map((route) => ({
    ...route,
    element: <Suspense fallback={<Loader />}>{route.element}</Suspense>,
    policy: RoutePolicy.ForAnyone,
  }));

const LoginPage = lazy(() =>
  import("../pages/login/LoginPage").then((m) => ({
    default: m.LoginPage,
  })),
);

const EnterprisePlanBooking = lazy(() =>
  import("../pages/enterprise-plan-booking/EnterprisePlanBookingPage").then(
    (m) => ({
      default: m.EnterprisePlanBooking,
    }),
  ),
);

const ScaitLandingPage = lazy(() =>
  import("../pages/scait-landing/ScaitLandingPage").then((m) => ({
    default: m.ScaitLandingPage,
  })),
);

const CompanySignupPage = lazy(() =>
  import("../pages/company-signup/CompanySignupPage").then((m) => ({
    default: m.CompanySignupPage,
  })),
);

const CompanyAccountPage = lazy(() =>
  import("../pages/company-account/CompanyAccountPage").then((m) => ({
    default: m.CompanyAccountPage,
  })),
);

const ChatbotPage = lazy(() =>
  import("../pages/chatbot/ChatbotPage").then((m) => ({
    default: m.ChatbotPage,
  })),
);

const PdfViewerPage = lazy(() =>
  import("../pages/chatbot/PdfViewerPage").then((m) => ({
    default: m.PdfViewerPage,
  })),
);

const PrePdfViewerPage = lazy(() =>
  import("../pages/chatbot/PrePdfViewerPage").then((m) => ({
    default: m.PrePdfViewerPage,
  })),
);

const PaymentSuccess = lazy(() =>
  import("../pages/payment/PaymentSuccess").then((m) => ({
    default: m.PaymentSuccess,
  })),
);

const PaymentFailure = lazy(() =>
  import("../pages/payment/PaymentFailure").then((m) => ({
    default: m.PaymentFailure,
  })),
);

const AuthProcess = lazy(() =>
  import("../pages/b2c-pages/AuthProcess").then((m) => ({
    default: m.AuthProcess,
  })),
);

const RegistrationSuccess = lazy(() =>
  import("../pages/b2c-pages/RegistrationSuccess").then((m) => ({
    default: m.RegistrationSuccess,
  })),
);

const PasswordResetSuccess = lazy(() =>
  import("../pages/b2c-pages/PasswordResetSuccess").then((m) => ({
    default: m.PasswordResetSuccess,
  })),
);

const FeedbackPage = lazy(() =>
  import("../pages/feedback/FeedbackPage").then((m) => ({
    default: m.FeedbackPage,
  })),
);

const SuperAdminPage = lazy(() =>
  import("../pages/super-admin/SuperAdminPage").then((m) => ({
    default: m.SuperAdminPage,
  })),
);

const CompanyDetailPage = lazy(() =>
  import("../pages/super-admin/CompanyDetailPage").then((m) => ({
    default: m.CompanyDetailPage,
  })),
);

const EvalJobDetailPage = lazy(() =>
  import("../pages/super-admin/eval/EvalJobDetailPage").then((m) => ({
    default: m.EvalJobDetailPage,
  })),
);

const ContactSupportPage = lazy(() =>
  import("../pages/contact-support/ContactSupportPage").then((m) => ({
    default: m.ContactSupportPage,
  })),
);

export const getAppRoutes = (links: AppLinks): RouteDefinition[] => {
  return [
    ...publicRoutes([
      { index: true, path: links.login(), element: <LoginPage /> },
      {
        path: links.authProcess(),
        element: <AuthProcess />,
      },
      {
        path: links.enterprisePlanBooking(),
        element: <EnterprisePlanBooking />,
      },
      {
        path: links.aboutScait(),
        element: <ScaitLandingPage />,
      },
      {
        path: links.contactSupport(),
        element: <ContactSupportPage />,
      },
    ]),
    ...privateRoutes([
      {
        path: links.home(),
        element: <HomeRedirect links={links} />,
      },
      {
        path: links.login(),
        element: <Navigate to={links.chatbot()} replace />,
      },
      {
        path: links.chatbot(),
        element: <ChatbotPage />,
      },
      {
        path: links.companyAccount(),
        element: <CompanyAccountPage />,
      },
      {
        path: links.companySignUp(),
        element: <CompanySignupPage />,
      },
      {
        index: true,
        path: links.pdf(),
        element: <PdfViewerPage />,
      },
      {
        index: true,
        path: links.prePdf(),
        element: <PrePdfViewerPage />,
      },
      {
        path: links.feedback(),
        element: <FeedbackPage />,
      },
    ]),
    ...anyoneRoutes([
      {
        path: links.paymentSuccess(),
        element: <PaymentSuccess />,
      },
      {
        path: links.paymentFailure(),
        element: <PaymentFailure />,
      },
      {
        path: links.registrationSuccess(),
        element: <RegistrationSuccess />,
      },
      {
        path: links.passwordResetSuccess(),
        element: <PasswordResetSuccess />,
      },
    ]),
    {
      path: links.superAdmin(),
      element: <SuperAdminPage />,
      policy: RoutePolicy.ForAnyone,
    },
    {
      path: "/super-admin/company/:companyId",
      element: <CompanyDetailPage />,
      policy: RoutePolicy.ForAnyone,
    },
    {
      path: links.superAdminEval(),
      element: <Navigate to={`${links.superAdmin()}#evalJobs`} replace />,
      policy: RoutePolicy.ForAnyone,
    },
    {
      path: "/super-admin/eval/:jobId",
      element: <EvalJobDetailPage />,
      policy: RoutePolicy.ForAnyone,
    },
  ];
};
